import React from "react";
import { Row, Col, Spinner } from "reactstrap";

/**
 * StatCards – Liste ekranlarının üstünde kullanılan özet kartı şeridi.
 *
 * Sunum bileşenidir: veri getirmez, hesap yapmaz. Üstteki renkli şerit kartı
 * birbirinden ayırır; ikon kullanılmaz.
 *
 * Bir item'a `onClick` verilirse kart tıklanabilir hale gelir (klavye ile de
 * çalışır); `active` seçili kartı vurgular. İkisi de opsiyoneldir.
 *
 * @param {Array<{key?: string, label: string, value: React.ReactNode, hint?: string,
 *   accent?: string, onClick?: () => void, active?: boolean, title?: string}>} items
 * @param {boolean} loading  Değerler yerine spinner gösterir
 * @param {string} className Dış Row'a eklenecek sınıf
 */

export const STAT_ACCENTS = {
    navy: "#221C5D",
    green: "#2E7D32",
    amber: "#E56A22",
    orange: "#E8703A",
    red: "#B42318",
    purple: "#6f42c1",
};

const STYLES = `
  .stat-cards .stat-card {
    position: relative;
    height: 100%;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 1rem;
    padding: 1.15rem 1.25rem 1rem;
    box-shadow: 0 1px 2px rgba(0,0,0,.04);
    overflow: hidden;
    transition: box-shadow .2s ease;
  }
  .stat-cards .stat-card:hover { box-shadow: 0 2px 6px rgba(0,0,0,.08); }
  .stat-cards .stat-card::before {
    content: "";
    position: absolute; top: 0; left: 0; right: 0; height: 4px;
    background: var(--stat-accent, #221C5D);
  }
  .stat-cards .stat-card-label {
    font-size: .72rem; font-weight: 700; letter-spacing: .04em;
    text-transform: uppercase; color: #8a94a6; margin-bottom: .45rem;
  }
  .stat-cards .stat-card-value {
    font-size: 1.85rem; font-weight: 700; color: #1e293b; line-height: 1.1;
  }
  .stat-cards .stat-card-hint { font-size: .8rem; color: #8a94a6; margin-top: .4rem; }

  .stat-cards .stat-card-clickable { cursor: pointer; }
  .stat-cards .stat-card-clickable:hover { border-color: var(--stat-accent, #221C5D); }
  .stat-cards .stat-card-clickable:focus-visible {
    outline: 2px solid var(--stat-accent, #221C5D); outline-offset: 2px;
  }
  .stat-cards .stat-card-active {
    border-color: var(--stat-accent, #221C5D);
    box-shadow: 0 0 0 1px var(--stat-accent, #221C5D), 0 1px 2px rgba(0,0,0,.04);
  }

  @media (max-width: 575.98px) {
    .stat-cards .stat-card { padding: .85rem .95rem .75rem; }
    .stat-cards .stat-card-value { font-size: 1.35rem; }
  }
`;

const StatCards = ({ items = [], loading = false, className = "" }) => {
    if (!items.length) return null;

    return (
        <Row className={`g-3 stat-cards ${className}`}>
            <style>{STYLES}</style>

            {items.map((item) => {
                const clickable = typeof item.onClick === "function";
                const interaction = clickable
                    ? {
                        role: "button",
                        tabIndex: 0,
                        "aria-pressed": !!item.active,
                        title: item.title,
                        onClick: item.onClick,
                        onKeyDown: (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                item.onClick();
                            }
                        },
                    }
                    : {};

                return (
                    <Col key={item.key || item.label} sm={6} xl={3}>
                        <div
                            className={[
                                "stat-card",
                                clickable ? "stat-card-clickable" : "",
                                item.active ? "stat-card-active" : "",
                            ].filter(Boolean).join(" ")}
                            style={{ "--stat-accent": item.accent || STAT_ACCENTS.navy }}
                            {...interaction}
                        >
                            <div className="stat-card-label">{item.label}</div>
                            <div className="stat-card-value">
                                {loading ? <Spinner size="sm" color="secondary" /> : item.value}
                            </div>
                            {item.hint && <div className="stat-card-hint">{item.hint}</div>}
                        </div>
                    </Col>
                );
            })}
        </Row>
    );
};

export default React.memo(StatCards);
